import {
    Checkbox,
    HStack,
    VStack,
    Text,
    Icon,
    useToast,
    Tr,
    InputGroup,
    InputLeftElement,
    Input,
    AlertDialog,
    AlertDialogBody,
    AlertDialogCloseButton,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
} from '@chakra-ui/react';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TbSearch,
    TbTrash,
    TbChevronRight,
    TbMapPin,
    TbPercentage,
    TbScale,
    TbBan,
    TbArrowMerge,
} from 'react-icons/tb';
import { useSettingsDraft } from '../../pages/settings/SettingsDraftContext';
import { useUpdateSettings } from '../../hooks/useUpdateSettings';
import PartBayButton from '../PartBayButton';
import Pagination from '../Pagination';
import BulkActionBar, { type BulkActionBarItem } from '../BulkActionBar';
import InsuranceBulkEditModal, {
    type InsuranceBulkField,
    type InsuranceBulkValue,
} from './InsuranceBulkEditModal';
import { InsuranceDrpSettings } from '../../hooks/useGetSettings';
import { useGetLocations } from '../../hooks/useGetLocations';
import {
    InlineLocationListCell,
    InlinePriceMatchCell,
    InlineProhibitedVendorsCell,
    toPriceMatchValue,
    type PriceMatchValue,
    type ProhibitedVendor,
} from './InsuranceInlineCells';
import type { LocationList } from './locationListValue';
import {
    applyRuleChange,
    buildRuleSets,
    groupParentLabel,
    mergeSubCompany,
    mergeTargetFor,
    removeRuleSet,
    ruleSetNames,
    type InsuranceRuleSet,
} from './insuranceRuleSets';
import {
    SettingsEmptyState,
    SettingsInlineNumberEdit,
    SettingsPageActions,
    SettingsRowActions,
    SettingsTable,
    SettingsTableFooter,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
} from './primitives';
import {
    PARTBAY_BLUE,
    SETTINGS_TABLE_GUTTER,
} from './primitives/constants';

interface InsuranceInterface {
    insurance: Array<InsuranceDrpSettings>;
}

/** How many sub company names fit under a carrier before the rest are counted. */
const SUB_COMPANY_NAME_LIMIT = 2;

/** "Includes SafeAuto, Esurance +2 more" — what the row's rules also cover. */
const subCompanySummary = (set: InsuranceRuleSet): string => {
    const names = set.included.map((sub) => sub.insurerName);
    const shown = names.slice(0, SUB_COMPANY_NAME_LIMIT).join(', ');
    const rest = names.length - SUB_COMPANY_NAME_LIMIT;
    return `Includes ${shown}${rest > 0 ? ` +${rest} more` : ''}`;
};

/**
 * DRP carriers, one row per set of rules rather than one per insurer. Carriers
 * in the same group that work the same way share a row: the parent owns the
 * configuration and the sub companies under it are listed beneath its name. A
 * sub company branched onto its own rules gets its own row, labelled
 * "Allstate -> SafeAuto" so it is clear where it came from.
 *
 * Inline cell edits and row deletes are buffered in the settings draft, so
 * nothing is written until the floating Save/Discard bar commits it; an edit is
 * applied to the whole set, which is what keeps a set identical by
 * construction. Adding a carrier and the full editor are the separate
 * full-screen flow, which owns its own save.
 */
const Insurance = ({ insurance }: InsuranceInterface) => {
    const navigate = useNavigate();
    // Provide default empty array if insurance is undefined or not an array
    const insuranceArray = Array.isArray(insurance) ? insurance : [];
    const { getDomain, setDomain, isDirty } = useSettingsDraft();
    const updateSettings = useUpdateSettings();
    /** The rule sets awaiting delete confirmation: one row, or a selection. */
    const [removeTargets, setRemoveTargets] = useState<InsuranceRuleSet[] | null>(null);
    /** Selected rows, by the insurer id of the carrier that owns the rules. */
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkField, setBulkField] = useState<InsuranceBulkField | null>(null);
    const removeCancelRef = useRef<HTMLButtonElement>(null);
    const localInsurance = getDomain('insurance', insuranceArray) ?? insuranceArray;
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const { scrollRef: tableScrollRef, pageSize: itemsPerPage } = useDynamicPageSize();

    const { data: locationsData } = useGetLocations();
    const locations = locationsData?.locations ?? [];
    const toast = useToast();

    // Each row carries the parent it belongs to, which for a carrier saved
    // before groups were tracked is worked out from its group rather than
    // stored on it.
    const rows = useMemo(
        () =>
            buildRuleSets(localInsurance).map((set) => ({
                set,
                parentLabel: groupParentLabel(set.primary, localInsurance),
            })),
        [localInsurance],
    );

    // A row matches on any carrier it covers and on its parent, so searching a
    // sub company or a group name finds the row instead of coming back empty.
    const filteredSets = useMemo(() => {
        if (!searchTerm) return rows;
        const term = searchTerm.toLowerCase();
        return rows.filter((row) =>
            [...ruleSetNames(row.set), row.parentLabel ?? ''].some((name) =>
                name.toLowerCase().includes(term),
            ),
        );
    }, [rows, searchTerm]);

    const totalPages = useMemo(() => Math.ceil(filteredSets.length / itemsPerPage), [filteredSets.length, itemsPerPage]);

    // The page size follows the viewport, so a resize can shrink the page
    // count; clamp so the user is never stranded on a now-empty page.
    useEffect(() => {
        setCurrentPage((prev) => Math.min(prev, Math.max(1, totalPages)));
    }, [totalPages]);

    const currentItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredSets.slice(startIndex, endIndex);
    }, [filteredSets, currentPage, itemsPerPage]);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    // Add and edit are the same full-screen carrier flow; 'new' is the add case.
    const handleAddCarrier = () => navigate('/settings/drp/new');

    const handleEditCarrier = (item: InsuranceDrpSettings) =>
        navigate(`/settings/drp/${item.insurerId}`);

    /**
     * Stage one row's change. POST /settings/update replaces the whole
     * insurance array, so the draft always holds the complete list; the change
     * lands on the primary and every sub company that follows it, which is what
     * keeps a set identical without asking the user to repeat themselves.
     */
    const stageChange = (set: InsuranceRuleSet, changes: Partial<InsuranceDrpSettings>) => {
        setDomain('insurance', applyRuleChange(localInsurance, set, changes));
    };

    const handleInlineThresholdSave = (set: InsuranceRuleSet, percent: number | null) => {
        stageChange(set, {
            orderingThreshold: percent === null ? undefined : percent / 100,
        });
    };

    const handleLocationsSave = (set: InsuranceRuleSet, next: LocationList) => {
        stageChange(set, { locations: next });
    };

    const handlePriceMatchSave = (set: InsuranceRuleSet, next: PriceMatchValue) => {
        stageChange(set, {
            priceMatchEstimate: next.estimate,
            priceMatchCCC: next.ccc,
            priceMatchPartstrader: next.partstrader,
            priceMatchCustom: next.custom,
            // The ranking itself is only editable on the carrier's screen;
            // turning custom off leaves it in place to switch back to.
            priceMatchCustomRanking: set.primary.priceMatchCustomRanking,
        });
    };

    const handleProhibitedVendorsSave = (set: InsuranceRuleSet, next: ProhibitedVendor[]) => {
        stageChange(set, { prohibitedSuppliers: next });
    };

    // Search can hide a selected row; drop those ids so the bulk bar never acts
    // on a carrier that is no longer on screen.
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(filteredSets.map((row) => row.set.primary.insurerId));
            const next = new Set(Array.from(prev).filter((id) => visible.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [filteredSets]);

    const selectedSets = useMemo(
        () => rows.filter((row) => selectedIds.has(row.set.primary.insurerId)).map((row) => row.set),
        [rows, selectedIds],
    );

    const pageIds = currentItems.map((row) => row.set.primary.insurerId);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    const somePageSelected = pageIds.some((id) => selectedIds.has(id));

    const handleToggleSelect = (insurerId: string, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(insurerId);
            else next.delete(insurerId);
            return next;
        });
    };

    // Header checkbox covers the current page only, matching what is visible.
    const handleSelectAllOnPage = (checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            pageIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
            return next;
        });
    };

    /**
     * Where each selected row would merge back to. Only offered when every one
     * of them is a branched sub company with a configured parent to rejoin —
     * merging half a selection would leave the bar lying about what it did.
     */
    const mergeTargets = useMemo(() => {
        if (selectedSets.length === 0) return null;
        const targets = selectedSets.map((set) => ({
            set,
            parent: mergeTargetFor(set.primary, localInsurance),
        }));
        return targets.every((entry) => entry.parent) ? targets : null;
    }, [selectedSets, localInsurance]);

    /**
     * Locations, threshold and remove stay in the open — they are what a
     * selection is usually made for. The rest sit under Other, which keeps the
     * bar from growing past a glance.
     */
    const bulkActions = useMemo<BulkActionBarItem[]>(() => {
        const actions: BulkActionBarItem[] = [
            { id: 'locations', label: 'Locations', icon: TbMapPin as React.ElementType },
            { id: 'threshold', label: 'Threshold', icon: TbPercentage as React.ElementType },
            {
                id: 'priceMatch',
                label: 'Price matching',
                icon: TbScale as React.ElementType,
                other: true,
            },
            {
                id: 'prohibitedVendors',
                label: 'Prohibited vendors',
                icon: TbBan as React.ElementType,
                other: true,
            },
        ];
        if (mergeTargets) {
            actions.push({
                id: 'merge',
                label: 'Merge with parent',
                icon: TbArrowMerge as React.ElementType,
                other: true,
            });
        }
        actions.push({
            id: 'remove',
            label: 'Remove',
            icon: TbTrash as React.ElementType,
            danger: true,
        });
        return actions;
    }, [mergeTargets]);

    const handleBulkAction = (actionId: string) => {
        if (actionId === 'remove') {
            setRemoveTargets(selectedSets);
            return;
        }
        if (actionId === 'merge') {
            handleBulkMerge();
            return;
        }
        setBulkField(actionId as InsuranceBulkField);
    };

    /** Fold every selected sub company back onto the rules of its parent. */
    const handleBulkMerge = () => {
        if (!mergeTargets) return;
        let next = localInsurance;
        mergeTargets.forEach(({ set, parent }) => {
            const parentEntry = next.find((entry) => entry.insurerId === parent?.insurerId);
            if (!parentEntry) return;
            // A merged sub company brings nothing of its own, so anything that
            // was riding on its rules follows it onto the parent's.
            [set.primary, ...set.included].forEach((entry) => {
                next = mergeSubCompany(next, entry.insurerId, parentEntry);
            });
        });
        setDomain('insurance', next);
        setSelectedIds(new Set());
        toast({
            title: `${mergeTargets.length} carrier${mergeTargets.length === 1 ? '' : 's'} merged`,
            description: 'They now follow their parent\'s rules. Save to apply.',
            status: 'info',
            duration: 3000,
            isClosable: true,
        });
    };

    /** One value, applied to every selected set and staged in the draft. */
    const handleBulkApply = (value: InsuranceBulkValue) => {
        let next = localInsurance;
        selectedSets.forEach((set) => {
            const changes: Partial<InsuranceDrpSettings> = {};
            if (value.locations !== undefined) changes.locations = value.locations;
            if (value.orderingThreshold !== undefined) {
                changes.orderingThreshold =
                    value.orderingThreshold === null ? undefined : value.orderingThreshold / 100;
            }
            if (value.priceMatch) {
                changes.priceMatchEstimate = value.priceMatch.estimate;
                changes.priceMatchCCC = value.priceMatch.ccc;
                changes.priceMatchPartstrader = value.priceMatch.partstrader;
                changes.priceMatchCustom = false;
                // The ranking stays put so a carrier can be switched back to it
                // from its own screen.
                changes.priceMatchCustomRanking = set.primary.priceMatchCustomRanking;
            }
            if (value.addProhibitedVendors) {
                const existing = set.primary.prohibitedSuppliers ?? [];
                const known = new Set(existing.map((vendor) => vendor._id));
                changes.prohibitedSuppliers = [
                    ...existing,
                    ...value.addProhibitedVendors.filter((vendor) => !known.has(vendor._id)),
                ];
            }
            if (Object.keys(changes).length > 0) next = applyRuleChange(next, set, changes);
        });
        setDomain('insurance', next);
        setBulkField(null);
        setSelectedIds(new Set());
    };

    /**
     * Deleting is committed by the confirmation dialog rather than staged: the
     * dialog is the confirmation, so there is nothing left to save. Only the
     * removal is written — other pending cell edits stay in the draft, with the
     * deleted rows dropped from it so a later save cannot resurrect them.
     */
    const handleConfirmRemove = () => {
        if (!removeTargets || removeTargets.length === 0) return;
        const sets = removeTargets;
        const carrierCount = sets.reduce((total, set) => total + 1 + set.included.length, 0);
        const strip = (list: Array<InsuranceDrpSettings>) =>
            sets.reduce((next, set) => removeRuleSet(next, set), list);

        updateSettings.mutate(
            { insurance: strip(insuranceArray) },
            {
                onSuccess: () => {
                    if (isDirty) setDomain('insurance', strip(localInsurance));
                    setRemoveTargets(null);
                    setSelectedIds(new Set());
                    toast({
                        title: carrierCount === 1 ? 'Carrier removed' : 'Carriers removed',
                        description:
                            sets.length === 1
                                ? `${sets[0].primary.insurerName}${
                                      sets[0].included.length
                                          ? ` and ${sets[0].included.length} sub ${sets[0].included.length === 1 ? 'company' : 'companies'}`
                                          : ''
                                  } removed`
                                : `${carrierCount} carriers removed`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                },
                onError: (error) => {
                    toast({
                        title: 'Error removing carrier',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    if (localInsurance.length === 0) {
        return (
            <SettingsEmptyState
                icon="shieldCheck"
                title="No insurance DRPs configured yet"
                description="Add your first insurance carrier to get started"
                action={{ label: 'Add Insurance', onClick: handleAddCarrier }}
            />
        );
    }

    return (
        <>
            <SettingsPageActions>
                <PartBayButton
                    size="sm"
                    variant="solid"
                    colorScheme="partbay-blue"
                    onClick={handleAddCarrier}
                    text="Add Insurance"
                />
            </SettingsPageActions>

            <HStack
                justify="space-between"
                align="center"
                gap={4}
                px={SETTINGS_TABLE_GUTTER}
                pb={4}
                flexShrink={0}
            >
                <InputGroup size="sm" maxW="500px">
                    <InputLeftElement>
                        <Icon as={TbSearch as React.ElementType} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search by name or type..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        bg="white"
                    />
                </InputGroup>
            </HStack>

            <SettingsTable
                scrollRef={tableScrollRef}
                head={
                    <>
                        <SettingsTh w="44px">
                            <Checkbox
                                size="sm"
                                colorScheme="partbay-blue"
                                isChecked={allPageSelected}
                                isIndeterminate={!allPageSelected && somePageSelected}
                                isDisabled={pageIds.length === 0}
                                onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                                aria-label="Select all carriers on this page"
                            />
                        </SettingsTh>
                        <SettingsTh>CARRIER</SettingsTh>
                        <SettingsTh minW="180px">LOCATIONS</SettingsTh>
                        <SettingsTh minW="230px">PARTSTRADER ORDERING THRESHOLD</SettingsTh>
                        <SettingsTh minW="180px">PRICE MATCHING</SettingsTh>
                        <SettingsTh minW="180px">PROHIBITED VENDORS</SettingsTh>
                        <SettingsTh textAlign="center">ACTIONS</SettingsTh>
                    </>
                }
            >
                {currentItems.map(({ set, parentLabel }) => {
                    const item = set.primary;
                    return (
                        // role="group" powers the chevron that fades in on
                        // hover for every editable cell in the row.
                        <Tr key={item.insurerId} role="group" _hover={{ bg: "gray.25" }}>
                            <SettingsTd>
                                <Checkbox
                                    size="sm"
                                    colorScheme="partbay-blue"
                                    isChecked={selectedIds.has(item.insurerId)}
                                    onChange={(e) =>
                                        handleToggleSelect(item.insurerId, e.target.checked)
                                    }
                                    aria-label={`Select ${item.insurerName}`}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                {/* The carrier name is the way into the full editor;
                                    every other column edits in place. A branched
                                    sub company carries its parent in front of it,
                                    and a parent lists what rides along under it. */}
                                <VStack align="flex-start" spacing={0.5}>
                                    <HStack spacing={1} minW={0}>
                                        {parentLabel && (
                                            <>
                                                <Text fontSize="sm" color="gray.500" noOfLines={1}>
                                                    {parentLabel}
                                                </Text>
                                                <Icon
                                                    as={TbChevronRight as React.ElementType}
                                                    boxSize={3.5}
                                                    color="gray.400"
                                                    flexShrink={0}
                                                />
                                            </>
                                        )}
                                        <Text
                                            as="button"
                                            fontSize="sm"
                                            textAlign="left"
                                            color="gray.800"
                                            _hover={{ color: PARTBAY_BLUE, textDecoration: 'underline' }}
                                            onClick={() => handleEditCarrier(item)}
                                        >
                                            {item.insurerName}
                                        </Text>
                                    </HStack>
                                    {set.included.length > 0 && (
                                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                            {subCompanySummary(set)}
                                        </Text>
                                    )}
                                </VStack>
                            </SettingsTd>
                            <SettingsTd>
                                <InlineLocationListCell
                                    value={item.locations ?? 'all'}
                                    locations={locations}
                                    ariaLabel={`Edit ${item.insurerName} locations`}
                                    onSave={(next) => handleLocationsSave(set, next)}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <SettingsInlineNumberEdit
                                    display={
                                        <Text fontSize="sm" color={item.orderingThreshold != null ? 'gray.700' : 'gray.400'}>
                                            {item.orderingThreshold != null ? `${Math.round(item.orderingThreshold * 100)}%` : '—'}
                                        </Text>
                                    }
                                    value={item.orderingThreshold != null ? Math.round(item.orderingThreshold * 100) : null}
                                    unit="%"
                                    min={0}
                                    max={100}
                                    placeholder="0"
                                    // A percentage needs a couple of characters, so the
                                    // editor stays narrow instead of matching the column.
                                    width="150px"
                                    minW="150px"
                                    ariaLabel={`Edit ${item.insurerName} PartsTrader ordering threshold`}
                                    onSave={(next) => handleInlineThresholdSave(set, next)}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <InlinePriceMatchCell
                                    value={toPriceMatchValue(item)}
                                    hasCustomRanking={(item.priceMatchCustomRanking?.length ?? 0) > 0}
                                    ariaLabel={`Edit ${item.insurerName} price matching`}
                                    onSave={(next) => handlePriceMatchSave(set, next)}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <InlineProhibitedVendorsCell
                                    value={item.prohibitedSuppliers ?? []}
                                    ariaLabel={`Edit ${item.insurerName} prohibited vendors`}
                                    onSave={(next) => handleProhibitedVendorsSave(set, next)}
                                />
                            </SettingsTd>
                            <SettingsTd textAlign="center">
                                <SettingsRowActions
                                    onEdit={() => handleEditCarrier(item)}
                                    onDelete={() => setRemoveTargets([set])}
                                />
                            </SettingsTd>
                        </Tr>
                    );
                })}
            </SettingsTable>

            <SettingsTableFooter>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    py={0}
                />
            </SettingsTableFooter>

            <BulkActionBar
                selectedCount={selectedSets.length}
                actions={bulkActions}
                onClearSelection={() => setSelectedIds(new Set())}
                onAction={handleBulkAction}
            />

            <InsuranceBulkEditModal
                field={bulkField}
                selectedCount={selectedSets.length}
                locations={locations}
                onApply={handleBulkApply}
                onClose={() => setBulkField(null)}
            />

            <AlertDialog
                isOpen={removeTargets !== null}
                leastDestructiveRef={removeCancelRef}
                onClose={() => setRemoveTargets(null)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="md">
                            {removeTargets && removeTargets.length > 1
                                ? 'Remove carriers'
                                : 'Remove carrier'}
                        </AlertDialogHeader>
                        <AlertDialogCloseButton />
                        <AlertDialogBody>
                            <Text fontSize="sm" color="gray.600">
                                This removes{' '}
                                {removeTargets && removeTargets.length === 1 ? (
                                    <>
                                        <Text as="span" fontWeight="semibold" color="gray.800">
                                            {removeTargets[0].primary.insurerName}
                                        </Text>
                                        {removeTargets[0].included.length > 0
                                            ? ` and ${removeTargets[0].included.length} sub ${
                                                  removeTargets[0].included.length === 1
                                                      ? 'company'
                                                      : 'companies'
                                              }`
                                            : ''}
                                    </>
                                ) : (
                                    <Text as="span" fontWeight="semibold" color="gray.800">
                                        {removeTargets?.reduce(
                                            (total, set) => total + 1 + set.included.length,
                                            0,
                                        )}{' '}
                                        carriers
                                    </Text>
                                )}
                                , along with their locations, thresholds and price matching. You
                                can add them back at any time.
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter gap={2}>
                            <Button
                                ref={removeCancelRef}
                                size="sm"
                                variant="outlineGray"
                                onClick={() => setRemoveTargets(null)}
                                isDisabled={updateSettings.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="red"
                                isLoading={updateSettings.isPending}
                                loadingText="Removing"
                                onClick={handleConfirmRemove}
                            >
                                Remove
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default Insurance;

